import { gql } from '@apollo/client';
import React, { useCallback, useEffect, useReducer, memo, useMemo } from 'react';
import { BadgeTheme, Stack } from '@moda/om';
import { useParams } from 'react-router-dom';
import { tc } from '../../lib/trackingContext';
import { deferUntilNewTitle } from '../../lib/deferUntilNewTitle';
import { ProductDetailsFragment } from '../../generated/types/ProductDetailsFragment';
import {
  PRODUCT_SHARE_FRAGMENT,
  PRODUCT_SHARE_VARIANT_FRAGMENT,
  ProductShare
} from '../ProductShare';
import { hrefFor } from '../../routers/PageRoutes';
import { useUrlParams } from '../../hooks/useUrlParams';
import { useAddToBag } from '../../hooks/useAddToBag';
import { AddToBagButton, ADD_TO_BAG_FRAGMENT } from '../AddToBagButton';
import { ColorSelector, COLOR_SELECTOR_FRAGMENT } from '../ColorSelector';
import { Link } from '../Link';
import { SizeGuideLink } from '../SizeGuideLink';
import {
  ProductCustomization,
  PRODUCT_CUSTOMIZATION_FRAGMENT,
  useProductCustomization
} from '../ProductCustomization';
import { PDPProductPrice } from '../PDPProductPrice';
import { SizeSelector, SIZE_SELECTOR_FRAGMENT } from '../SizeSelector';
import { SizeInventoryFragment } from '../../generated/types/SizeInventoryFragment';
import { Favorite } from '../../components/Favorite';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { PdpInfoContainer, PDP_INFO_MODULE_FRAGMENT } from '../PdpInfoContainer';
import { ProductDetailsVariantFragment } from '../../generated/types/ProductDetailsVariantFragment';
import { PriceCollectionBadge } from '../CollectionBadge';
import { CollectionBadgeType, useCollectionBadges } from '../../hooks/useCollectionBadges';
import { ProductDetailsFinalSaleInformation } from './ProductDetailsFinalSaleInformation/ProductDetailsFinalSaleInformation';
import { ProductDetailsSizeInventory } from './ProductDetailsSizeInventory';
import {
  PRODUCT_DETAILS_EXPANDED_FRAGMENT,
  PRODUCT_DETAILS_EXPANDED_VARIANT_FRAGMENT,
  ProductDetailsExpanded
} from './ProductDetailsExpanded';
import { EditorsNote } from './EditorsNote';
import {
  COLOR_SWITCHER_DESCRIPTION_VARIANT_FRAGMENT,
  ColorSwitcherDescription
} from './ColorSwitcherDescription';
import { ProductDetailsTagsSection, PDP_BADGES_FRAGMENT } from './ProductDetailsTagsSection';

import './ProductDetails.scss';

const PRODUCT_DETAILS_VARIANT_FRAGMENT = gql`
  fragment ProductDetailsVariantFragment on VariantData {
    id
    color
    collections
    soldOut: sold_out
    returnPolicy: return_policy
    inventory {
      __typename
    }
    prices(country_code: $countryCode) {
      originalPrice: original_price {
        currency
        price
      }
      currentPrice: current_price {
        currency
        price
      }
      depositPrice: deposit_price {
        currency
        price
      }
      vat {
        currency
        price
      }
    }
    ...PDPBadgesFragment
    ...ProductShareVariantFragment
    ...ColorSwitcherDescriptionVariantFragment
    ...ProductDetailsExpandedVariantFragment
  }

  ${PRODUCT_SHARE_VARIANT_FRAGMENT}
  ${PDP_BADGES_FRAGMENT}
  ${COLOR_SWITCHER_DESCRIPTION_VARIANT_FRAGMENT}
  ${PRODUCT_DETAILS_EXPANDED_VARIANT_FRAGMENT}
`;

export const PRODUCT_DETAILS_FRAGMENT = gql`
  fragment ProductDetailsFragment on Variant {
    name
    tag
    designerName: designer_name
    designerSlug: designer_slug
    available
    availability
    category
    customizable
    customizationParameters: customization_parameters {
      ...ProductCustomizationFragment
    }
    sizeEquivalents: size_equivalents
    masterVariant: master_variants_data {
      ...ProductDetailsVariantFragment
    }
    otherVariants: other_variants_data {
      ...ProductDetailsVariantFragment
    }
    ...ColorSelectorFragment
    ...SizeSelectorFragment
    ...AddToBagFragment

    ...ProductDetailsExpandedFragment
    ...PdpInfoContainerFragment
    ...ProductShareFragment
  }
  ${COLOR_SELECTOR_FRAGMENT}
  ${SIZE_SELECTOR_FRAGMENT}
  ${ADD_TO_BAG_FRAGMENT}
  ${PRODUCT_DETAILS_VARIANT_FRAGMENT}
  ${PRODUCT_DETAILS_EXPANDED_FRAGMENT}
  ${PRODUCT_SHARE_FRAGMENT}
  ${PDP_INFO_MODULE_FRAGMENT}
  ${PRODUCT_CUSTOMIZATION_FRAGMENT}
`;

interface Props {
  variant: ProductDetailsFragment;
}

interface State {
  size: SizeInventoryFragment | null;
  hoveredColor: string | undefined;
}

type Action =
  | { type: 'SELECT_SIZE'; payload: { size: SizeInventoryFragment | null } }
  | { type: 'CLEAR_COLOR_HOVER' }
  | { type: 'HOVER_COLOR'; payload: { color: string } };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT_SIZE':
      return { ...state, size: action.payload.size };
    case 'HOVER_COLOR':
      return { ...state, hoveredColor: action.payload.color };
    case 'CLEAR_COLOR_HOVER':
      return { ...state, hoveredColor: undefined };
  }
};

export const ProductDetails: React.FC<Props> = memo(({ variant }) => {
  const {
    customizationMessage,
    customizationMessageError,
    handleSetCustomizationMessage,
    isCustomizationValid
  } = useProductCustomization(variant.customizationParameters);
  const { vertical = 'women' } = useParams<{ vertical?: string }>();

  const { params, setParams } = useUrlParams();

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', variant.id), [variant]);

  const priceBadge = useCollectionBadges(activeVariant.collections, CollectionBadgeType.Price)
    ?.analyticsProperty;

  const { isOneSizedItem } = useAddToBag({
    activeVariantId: activeVariant.id,
    variant,
    validatesSizePresenceOn: 'always'
  });

  const size = useMemo(() => {
    if (isOneSizedItem) {
      return activeVariant.inventory[0];
    }

    if (params.size) {
      const matchedSize = activeVariant.inventory.find(
        inventoryItem => inventoryItem?.size === params.size
      );

      if (matchedSize) {
        return matchedSize;
      }
    }
    return null;
  }, [activeVariant.inventory, isOneSizedItem, params]);

  const [state, dispatch] = useReducer(reducer, {
    size,
    hoveredColor: undefined
  });

  const handleSelectSize = useCallback(
    (option: SizeInventoryFragment) =>
      option.size &&
      dispatch({
        type: 'SELECT_SIZE',
        payload: { size: { ...option, available: option.available || 0 } }
      }),
    []
  );

  const handleSelectColor = useCallback(
    (color: string) => {
      const variantForColor = getActiveVariantBy(variant, 'color', color);

      if (activeVariant.id !== variantForColor.id) {
        setParams({ ...params, variantId: variantForColor?.id });
      }
    },
    [activeVariant, variant, params, setParams]
  );

  const handleColorMouseEnter = useCallback((color: string) => {
    dispatch({ type: 'HOVER_COLOR', payload: { color } });
  }, []);

  const clearColorHover = useCallback(() => {
    dispatch({ type: 'CLEAR_COLOR_HOVER' });
  }, []);

  const colorDetailsVariant = state.hoveredColor
    ? getActiveVariantBy<ProductDetailsVariantFragment>(variant, 'color', state.hoveredColor)
    : activeVariant;

  useEffect(() => {
    // Set the current variant here so that it's available to usePageTracking in PageLayout.tsx
    tc.setCurrent('variant', variant);
    deferUntilNewTitle(() => {
      tc.track('Product Viewed', {
        addToScopeOnce: {
          activeVariant,
          size: state.size,
          badge: priceBadge
        }
      });
    });

    return () => tc.setCurrent('variant', undefined);
    // Explicitly run only once, on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant.id]);

  return (
    <div className="ProductDetails">
      <ProductDetailsTagsSection tag={variant.tag} badges={activeVariant.badges as BadgeTheme[]} />

      {variant.designerSlug && (
        <Link
          className="ProductDetails__designer"
          to={hrefFor.DesignerPage({
            designerSlug: variant.designerSlug,
            vertical: vertical
          })}
        >
          {variant.designerName}
        </Link>
      )}

      <h1 className="ProductDetails__name">{variant.name}</h1>

      {activeVariant.prices && (
        <PDPProductPrice
          className="ProductDetails__price"
          depositPrice={activeVariant.prices.depositPrice}
          originalPrice={activeVariant.prices.originalPrice}
          currentPrice={activeVariant.prices.currentPrice}
          vatPrice={activeVariant.prices.vat}
        >
          <PriceCollectionBadge collections={activeVariant.collections} />
        </PDPProductPrice>
      )}

      <ColorSwitcherDescription colorDetailsVariant={colorDetailsVariant} />

      <ColorSelector
        className="ProductDetails__color-selector"
        variant={variant}
        onMouseEnter={handleColorMouseEnter}
        onMouseLeave={() => clearColorHover()}
        onSelect={handleSelectColor}
        selected={activeVariant.color}
      />

      {variant.available && !isOneSizedItem && (
        <>
          <div className="ProductDetails__size">
            <ProductDetailsSizeInventory selectedSize={state.size} />

            <SizeGuideLink category={variant.category} sizeEquivalents={variant.sizeEquivalents} />
          </div>

          {activeVariant.inventory.length > 0 && (
            <SizeSelector
              className="ProductDetails__sizes"
              variant={variant}
              activeVariantId={activeVariant.id}
              selected={state.size}
              onSelect={handleSelectSize}
            />
          )}
        </>
      )}

      {!variant.available && <div className="ProductDetails__sold-out">Sold Out</div>}

      {variant.customizable && variant.customizationParameters && (
        <ProductCustomization
          customizationMessage={customizationMessage}
          customizationParameters={variant.customizationParameters}
          customizationMessageError={customizationMessageError}
          onChange={handleSetCustomizationMessage}
        />
      )}

      <PdpInfoContainer variant={variant} />

      <Stack space={2}>
        {variant.available && (
          <AddToBagButton
            activeVariantId={activeVariant.id}
            customization={customizationMessage}
            isCustomizationValid={isCustomizationValid}
            size={state.size}
            validatesSizePresenceOn="submit"
            variant={variant}
          />
        )}

        <Favorite type="BUTTON" variantId={variant.id} />

        <ProductDetailsFinalSaleInformation returnPolicy={activeVariant.returnPolicy} />
      </Stack>

      <EditorsNote variant={variant} />

      <ProductDetailsExpanded activeVariant={activeVariant} variant={variant} />

      <ProductShare activeVariant={activeVariant} variant={variant} />
    </div>
  );
});
